import { signalApi } from "../js/axios";
import { HubConnectionState } from "@microsoft/signalr";
const signalR = require("@microsoft/signalr");

const makeConnectionKey = (baseUrl, endpoint) =>
  `${String(baseUrl).replace(/\/+$/, "")}/${String(endpoint).replace(
    /^\/+/,
    ""
  )}`;

const base_url = signalApi;

const makeCacheKey = (connectionKey, view) => `${connectionKey}::${view}`;

export const SignalRConnector = {
  _connectionCache: new Map(),

  async startSignalR(
    endpoint,
    startListenersCallback,
    timeout = 0,
    userId,
    view
  ) {
    const connectionKey = makeConnectionKey(base_url, endpoint);
    const cacheKey = makeCacheKey(connectionKey, view);

    let entry = this._connectionCache.get(cacheKey);
    const cached = entry?.connection;

    // 1) se já tem uma startPromise rolando, apenas aguarde
    if (entry?.startPromise) {
      await entry.startPromise;
      return entry.connection;
    }

    // 2) reuse conexão existente
    if (cached) {
      if (cached.state === HubConnectionState.Connected) {
        if (startListenersCallback && !entry.listenersInitialized) {
          if (timeout > 0) await new Promise((r) => setTimeout(r, timeout));
          startListenersCallback(cached);
          entry.listenersInitialized = true;
        }
        return cached;
      }
      if (
        cached.state === HubConnectionState.Connecting ||
        cached.state === HubConnectionState.Reconnecting
      ) {
        await this._waitForConnected(cached, 20000);
        return cached;
      }
    }

    // 3) criar nova conexão e registrar a startPromise no cache ANTES de iniciar
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(connectionKey)
      .withAutomaticReconnect([0, 5000, 10000, 15000, 20000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    entry = { connection, listenersInitialized: false, startPromise: null };
    this._connectionCache.set(cacheKey, entry);

    entry.startPromise = (async () => {
      if (startListenersCallback && !entry.listenersInitialized) {
        if (timeout > 0) await new Promise((r) => setTimeout(r, timeout));
        startListenersCallback(connection);
        entry.listenersInitialized = true;
      }

      await this._startConnection(connection, userId);

      connection.onreconnected(async () => {
        await this._bindIdentity(connection, userId).catch(() => {});
      });
    })();

    await entry.startPromise;
    entry.startPromise = null;

    return connection;
  },

  async _startConnection(connection, userId) {
    await connection.start();
    await this._bindIdentity(connection, userId);
  },

  async _bindIdentity(connection, userId) {
    await connection.invoke("getConnectionId").catch(() => {});
    if (userId != null)
      await connection.invoke("SetUser", userId).catch(() => {});
  },

  async _waitForConnected(connection, timeoutMs = 15000) {
    if (connection.state === HubConnectionState.Connected) return;

    const start = Date.now();
    await new Promise((resolve, reject) => {
      const onClose = (err) => reject(err || new Error("Closed while waiting"));
      connection.onclose(onClose);

      const tick = () => {
        if (connection.state === HubConnectionState.Connected) {
          connection.off("close", onClose);
          return resolve();
        }
        if (Date.now() - start >= timeoutMs) {
          connection.off("close", onClose);
          return reject(new Error("Timeout"));
        }
        setTimeout(tick, 200);
      };
      tick();
    });
  },

  async stopSignalR(target, view) {
    let connectionKey, cacheKey, connection;

    if (typeof target === "string") {
      connectionKey = makeConnectionKey(base_url, target);
      cacheKey = makeCacheKey(connectionKey, view);
      connection = this._connectionCache.get(cacheKey)?.connection;
    } else if (target && typeof target === "object" && "state" in target) {
      connection = target;
      for (const [k, v] of this._connectionCache.entries()) {
        if (v.connection === connection) {
          cacheKey = k;
          break;
        }
      }
    }

    if (!connection) return;

    if (connection.state !== HubConnectionState.Disconnected) {
      await connection.stop().catch(() => {});
    }
    if (cacheKey) this._connectionCache.delete(cacheKey);
  }
};

export default function (app) {
  app.config.globalProperties.$SignalRConnector = SignalRConnector;
}
